import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { EditAutomationFunctionsComponent } from 'src/app/components/edit-automation-functions/edit-automation-functions.component';
import { SlackMessageComponent } from 'src/app/components/slack-message/slack-message.component';
import { SlackTextEditorComponent } from 'src/app/components/slack-text-editor/slack-text-editor.component';
import {
  AUTOMATION_ACTION_TYPES_ICONS,
  AUTOMATION_ACTION_TYPES_LABELS,
  STATUS_COLUMN_FORMAT_MAP,
  SUPPORT_STEP_ASSIGNEE_TYPES,
  DEFAULT_PRIORITY_TOKENS,
} from 'src/app/constants';
import { extractTicketId } from 'src/app/utils/custom-fields';
import { isHtml, getOrderedPriorityOptions } from 'src/app/utils/utils';
import {
  AUTOMATION_ACTION_TYPES,
  CUSTOM_FIELD_TYPES,
  ListResponse,
  CustomField,
  PriorityTokens,
  SlackChannel,
} from 'src/models';
import { ENTITY_TYPES } from 'src/models/entities';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { AutomationsService } from 'src/services/automations.service';
import { CustomFieldsService } from 'src/services/custom-fields.service';
import { EmojisService } from 'src/services/emojis.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TokenService } from 'src/services/token.service';

import { EmojiPickerComponent } from '../../components/emoji-picker/emoji-picker.component';
import { AutomationCustomFieldInputComponent } from '../automation-custom-field-input/automation-custom-field-input.component';

const STANDARD_RESOLVABLE_ENTITY_FIELDS: any[] = [
  {
    id: 'conversation.submitterUserId',
    title: 'Submitter',
    object: {
      type: ENTITY_TYPES.USER,
    },
  },
  {
    id: 'conversation.assignedToUserId',
    title: 'Assignee',
    object: {
      type: ENTITY_TYPES.USER,
    },
  },
  {
    id: 'user.id',
    title: 'Triggering User',
    object: {
      type: ENTITY_TYPES.USER,
    },
  },
];

// Event labels for formatting
const AUTOMATION_TRIGGER_OBJECT_TYPES = {
  CONVERSATION: 'conversation',
} as const;

const AUTOMATION_TRIGGER_EVENTS = {
  [AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION]: {
    CREATED: 'created',
    UPDATED: 'updated',
    APPROVAL_REQUEST_APPROVED: 'approvalRequestApproved',
    APPROVAL_REQUEST_REJECTED: 'approvalRequestRejected',
    APPROVAL_REQUEST_CREATED: 'approvalRequestCreated',
    MESSAGE_ADDED: 'messageAdded',
    INTERNAL_MESSAGE_ADDED: 'internalMessageAdded',
    EXTERNAL_MESSAGE_ADDED: 'externalMessageAdded',
    CLOSED: 'closed',
    REOPENED: 'reopened',
    PUT_ON_HOLD: 'putOnHold',
    INTERNAL_UNRESPONSIVE: 'internalUnresponsive',
    EXTERNAL_UNRESPONSIVE: 'externalUnresponsive',
    TRIAGED: 'triaged',
    TRIAGE_MESSAGE_ADDED: 'triageMessageAdded',
    ESCALATION_CREATED: 'escalationCreated',
    ESCALATION_ACKNOWLEDGED: 'escalationAcknowledged',
    ESCALATION_RESOLVED: 'escalationResolved',
  },
} as const;

@Component({
  selector: 'app-automation-action-step',
  templateUrl: './automation-action-step.component.html',
  styleUrls: ['./automation-action-step.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    SlackTextEditorComponent,
    SlackMessageComponent,
    NgxTippyModule,
    AutomationCustomFieldInputComponent,
    ChannelSelectorModule,
    EmojiPickerComponent,
    RouterModule,
  ],
})
export class AutomationActionStepComponent implements OnInit, OnChanges {
  @Input() index: number;
  @Input() step: any;
  @Input() lookups: Record<string, Record<string, string>> = {};
  @Input() functionNames: string[] = [];
  @Input() functionsVersions: any[] = [];
  @Input() canDrag = false;
  @Input() inputFields: any[] = [];
  @Input() projectId: string | null = null;
  @Input() triggerType: string | null = null;
  @Output() remove = new EventEmitter<void>();
  @Output() actionDidChange = new EventEmitter<void>();

  priorityLabels: Record<string, PriorityTokens> = DEFAULT_PRIORITY_TOKENS;
  priorityOptionsForTemplate: { value: number; label: string }[] = [];

  public AUTOMATION_ACTION_TYPES = AUTOMATION_ACTION_TYPES;
  public AUTOMATION_ACTION_TYPES_ICONS = AUTOMATION_ACTION_TYPES_ICONS;
  public AUTOMATION_ACTION_TYPES_LABELS = AUTOMATION_ACTION_TYPES_LABELS;

  editorOptions = {
    theme: 'vs-dark',
    language: 'typescript',
    automaticLayout: true,
  };
  code: string = '';
  isLoadingLookups = false;

  ENTITY_TYPES = ENTITY_TYPES;

  // Stored hydrated value for the custom field input
  hydratedCustomFieldValue: any = null;

  // Stored hydrated value for the emoji input
  hydratedEmojiValue: any = null;

  selectedCustomFieldForAction: CustomField | null = null;

  public isHTML = isHtml;

  // Toggle for advanced settings in wait action
  showAdvancedSettings = false;

  constructor(
    private searchablePopoverService: SearchablePopoverService,
    private modalCtrl: ModalController,
    private automationsService: AutomationsService,
    private msgSrvc: MessageService,
    private popoverCtrl: PopoverController,
    private apiService: ApiService,
    private customFieldsService: CustomFieldsService,
    private tokenService: TokenService,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    public authService: AuthService,
    private emojiService: EmojisService,
  ) {}

  async ngOnInit() {
    // Lazy load template html as needed
    await this.loadPriorityLabels();

    if (this.step?.data?.action?.data?.templateId) {
      this.apiService.get(`/templates/${this.step.data.action.data.templateId}`).subscribe(async (template: any) => {
        setTimeout(() => {
          if (!this.lookups.templateHtml) {
            this.lookups.templateHtml = {};
          }
          this.lookups.templateHtml[this.step.data.action.data.templateId] = template.html;
        }, 100);
      });
    }
    if (this.step?.data?.action?.data?.emoji) {
      this.hydratedEmojiValue = await this.emojiService.resolveEmojiToDisplay(this.step.data.action.data.emoji);
    }

    // Initialize state if the current step is SET_CUSTOM_FIELD
    await this.initializeSetCustomFieldState();

    // Initialize isAutoresponse default value for SEND_SLACK_TEMPLATE actions
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.SEND_SLACK_TEMPLATE) {
      if (this.step.data.action.data.isAutoresponse === undefined) {
        this.step.data.action.data.isAutoresponse = true;
      }
    }

    // Initialize priority mapping for CREATE_JIRA_TASK actions
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.CREATE_JIRA_TASK) {
      if (!this.step.data.action.data.priorityMapping) {
        this.step.data.action.data.priorityMapping = {};
      }
    }

    // Load code if it's a trigger function
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.TRIGGER_FUNCTION) {
      this.code = this.step.data.action.data.code;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If the step data OR the lookups change externally, re-initialize
    if (changes['step'] || changes['lookups']) {
      // Only proceed if the step type is relevant
      if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD) {
        // Avoid running hydration if the selected field hasn't changed and lookups haven't significantly changed (simple check)
        // This prevents potential loops if hydration itself causes further changes down the line.
        const lookupsChanged =
          changes['lookups'] && changes['lookups'].previousValue !== changes['lookups'].currentValue;
        const stepChanged = changes['step'] && changes['step'].previousValue !== changes['step'].currentValue;

        // Re-hydrate if the step changed OR if lookups changed (and we have a field selected)
        if (stepChanged || (lookupsChanged && this.selectedCustomFieldForAction)) {
          this.initializeSetCustomFieldState();
        }
      }
    }
  }

  // Initialize state based on current step data
  async initializeSetCustomFieldState() {
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD) {
      const fieldId = this.step.data.action.data.customFieldId;
      if (fieldId) {
        const field = await this.customFieldsService.getById(fieldId);
        this.selectedCustomFieldForAction = field;
        // Hydrate the value for the child input component
        this.hydrateValueForInput();
      } else {
        this.selectedCustomFieldForAction = null;
        this.hydratedCustomFieldValue = null;
      }
      this.changeDetectorRef.markForCheck(); // Ensure UI updates if needed
    }
  }

  // Hydrates the value stored as ID(s) into full object(s) for the child input
  private hydrateValueForInput(): void {
    const storedValue = this.step?.data?.action?.data?.value;
    const field = this.selectedCustomFieldForAction;

    if (!field || storedValue === null || storedValue === undefined) {
      this.hydratedCustomFieldValue = storedValue; // Pass null/undefined/primitives directly
      return;
    }

    const hydrate = (id: string): any => {
      const stringId = String(id); // Ensure string key for lookups
      switch (field.type) {
        case CUSTOM_FIELD_TYPES.USER_SELECT:
        case CUSTOM_FIELD_TYPES.MULTI_USER_SELECT:
          return { id: stringId, name: this.lookups['assigneeId']?.[stringId] || stringId };
        case CUSTOM_FIELD_TYPES.USER_GROUP_SELECT:
        case CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT:
          const name = this.lookups['groupId']?.[stringId] ?? this.lookups['userGroupId']?.[stringId] ?? stringId;
          return { id: stringId, name: name };
        case CUSTOM_FIELD_TYPES.SINGLE_SELECT:
        case CUSTOM_FIELD_TYPES.MULTI_SELECT:
          if (field.externalSelect) {
            // For external, we need {value, label}
            // Attempt to find label in lookups (might be populated by edit-automation)
            const label = this.lookups['externalSelect']?.[stringId] ?? stringId;
            return { value: stringId, label: label };
          } else {
            // Internal select stores primitive value directly
            return id; // Return the primitive value itself
          }
        default:
          return id; // Return primitive for other types (text, date, checkbox)
      }
    };

    if (Array.isArray(storedValue)) {
      this.hydratedCustomFieldValue = storedValue.map(hydrate).filter((v) => v !== null && v !== undefined); // Hydrate each ID in array
    } else {
      this.hydratedCustomFieldValue = hydrate(storedValue); // Hydrate single ID
    }
  }

  async loadPriorityLabels() {
    const resolvedTokens = await this.tokenService.getResolvedTokens(['PRIORITY_LABEL_MAP'], undefined);
    this.priorityLabels = resolvedTokens['PRIORITY_LABEL_MAP'] || DEFAULT_PRIORITY_TOKENS;
    this.priorityOptionsForTemplate = getOrderedPriorityOptions(this.priorityLabels);
  }

  selectAssignee(actionData: any) {
    this.searchablePopoverService.assignees({
      event,
      showCreatedByUserOption: false,
      showPrimaryRepOption: false,
      showNoneOption: false,
      callback: async (id: string, _name: string, object: any) => {
        actionData.entityId = id;
        actionData.entityType =
          object.type !== SUPPORT_STEP_ASSIGNEE_TYPES.USER
            ? SUPPORT_STEP_ASSIGNEE_TYPES.GROUP
            : SUPPORT_STEP_ASSIGNEE_TYPES.USER;

        // Keep track in lookups
        if (!this.lookups.assigneeId) {
          this.lookups.assigneeId = {};
        }
        if (!this.lookups.groupId) {
          this.lookups.groupId = {};
        }

        if (actionData.entityType === SUPPORT_STEP_ASSIGNEE_TYPES.USER) {
          this.lookups.assigneeId[id] = object.name;
        } else {
          this.lookups.groupId[id] = object.name;
        }
      },
    });
  }

  selectSubStatus(actionData: any) {
    this.searchablePopoverService.subStatusKeys({
      event,
      projectId: this.projectId,
      callback: async (id: string, name: string) => {
        actionData.subStatusKey = id;

        if (!this.lookups.subStatusKey) {
          this.lookups.subStatusKey = {};
        }

        this.lookups.subStatusKey[id] = name;
      },
    });
  }

  getSubStatusIcon(subStatusKey: string) {
    const type = subStatusKey.split('__')[0];

    return STATUS_COLUMN_FORMAT_MAP[type]?.icon;
  }

  getSubStatusIconClass(subStatusKey: string) {
    const type = subStatusKey.split('__')[0];

    return STATUS_COLUMN_FORMAT_MAP[type]?.iconClass;
  }

  selectTag(actionData: any) {
    this.searchablePopoverService.tags({
      event,
      projectId: this.projectId ?? undefined,
      callback: async (id: string, _name: string, object: any) => {
        const list = actionData.tagIds || [];
        if (list.includes(id)) {
          return;
        }

        actionData.tagIds = [...list, id];

        if (!this.lookups.tagId) {
          this.lookups.tagId = {};
        }

        this.lookups.tagId[id] = object.name;
      },
    });
  }

  removeTag(actionData: any, index: number) {
    actionData.tagIds.splice(index, 1);
  }

  selectTicketType(actionData: any) {
    this.searchablePopoverService.ticketTypes({
      event,
      projectId: this.projectId,
      callback: async (id: string, _name: string, object: any) => {
        actionData.ticketTypeId = id;

        if (!this.lookups.ticketTypeId) {
          this.lookups.ticketTypeId = {};
        }

        this.lookups.ticketTypeId[id] = object.name;
      },
    });
  }

  selectCollaboratorType(actionData: any) {
    this.searchablePopoverService.collaboratorTypes({
      event,
      callback: async (id: string, name: string, object: any) => {
        actionData.collaboratorTypeId = id;

        if (!this.lookups.collaboratorTypeId) {
          this.lookups.collaboratorTypeId = {};
        }

        this.lookups.collaboratorTypeId[id] = name;
      },
    });
  }

  selectSlackUserGroup(actionData: any) {
    this.searchablePopoverService.slackUserGroups({
      event,
      callback: async (id: string, _name: string, object: any) => {
        actionData.slackUserGroupId = id;

        if (!this.lookups.slackGroupId) {
          this.lookups.slackGroupId = {};
        }

        this.lookups.slackGroupId[id] = object.name;
      },
    });
  }

  selectUserForList(actionData: any) {
    this.searchablePopoverService.users({
      event,
      showAssignToMeText: '',
      showNoneOption: false,
      callback: async (id: string, _name: string, object: any) => {
        if (actionData.userIds.includes(id)) {
          return;
        }

        actionData.userIds.push(id);

        if (!this.lookups.assigneeId) {
          this.lookups.assigneeId = {};
        }

        this.lookups.assigneeId[id] = object.name;
      },
    });
  }

  selectTemplate(actionData: any) {
    this.searchablePopoverService.templates({
      event,
      showNoneOption: true,
      selectedTemplateId: actionData.templateId,
      projectId: this.projectId ?? undefined,
      callback: (id: string, _title: string, data: any) => {
        actionData.templateId = id;

        if (!this.lookups.templateId) {
          this.lookups.templateId = {};
        }

        if (!this.lookups.templateHtml) {
          this.lookups.templateHtml = {};
        }

        this.lookups.templateId[id] = data.name;
        this.lookups.templateHtml[id] = data.html;
      },
    });
  }

  selectSlackChannel(actionData: any) {
    this.searchablePopoverService.slackChannels({
      event,
      callback: async (id: string, _name: string, object: any) => {
        actionData.channelId = id;

        if (!this.lookups.slackChannelId) {
          this.lookups.slackChannelId = {};
        }

        this.lookups.slackChannelId[id] = object.name;
      },
    });
  }

  onSlackChannelChange(channel: SlackChannel) {
    this.step.data.action.data.channelId = channel.id;
    this.lookups.slackChannelId[channel.id] = channel.name;
  }

  removeUserFromList(actionData: any, index: number) {
    actionData.userIds.splice(index, 1);
  }

  // If your action is "triggerFunction" and you had an Edit Functions modal:
  async editFunctions() {
    const customActionsVersion = this.getFunctionsVersion('draft') ?? this.getFunctionsVersion('active');

    const modal = await this.modalCtrl.create({
      component: EditAutomationFunctionsComponent,
      cssClass: 'modal-full-screen',
      componentProps: {
        version: customActionsVersion.version,
        status: customActionsVersion.status,
        projectId: this.projectId,
      },
      keyboardClose: false,
      backdropDismiss: false,
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getFunctionsVersions();
    });
  }

  async getFunctionsVersions() {
    try {
      const functionsVersionsResponse: any = await this.automationsService.getAllFunctionsVersions(this.projectId);

      this.functionsVersions = functionsVersionsResponse;
      this.functionNames = this.getFunctionsVersion('active').exportedFunctionsByType.automationAction;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  getFunctionsVersion(status: string) {
    return (this.functionsVersions ?? []).find((version) => version.status === status);
  }

  handleActionChange() {
    // Could pass the entire action object to parent if needed
    this.actionDidChange.emit();
  }

  async handleOptionsClick() {
    const options: ActionDropdownType[] = [
      {
        label: 'Remove',
        icon: 'trash-outline',
        destructive: true,
      },
    ];

    // Someday:
    // {
    //   label: 'Move up',
    //   icon: 'arrow-up-outline',
    // },
    // {
    //   label: 'Move down',
    //   icon: 'arrow-down-outline',
    // },

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number, label: string) => {
          popover.dismiss();

          this.remove.emit();
        },
      },
      event,
    });
    popover.present();
  }

  toggleWorkingTimeOnly(actionData: any) {
    actionData.wait.workingTimeOnly = !actionData.wait.workingTimeOnly;
  }

  toggleCreateTemporaryTriageChannel(actionData: any) {
    if (!actionData.createChannel) {
      actionData.createChannel = {
        prefix: 'triage-',
        isPrivate: false,
      };
      actionData.channelId = undefined;
    } else {
      actionData.createChannel = undefined;
    }
  }

  compareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  selectResolvableEntity(
    actionData: any,
    fieldId: string,
    entityType?: typeof ENTITY_TYPES.USER | typeof ENTITY_TYPES.USER_GROUP,
  ) {
    const selectedValue = actionData[fieldId];

    this.searchablePopoverService.showPopover({
      event,
      showImages: false,
      showSearch: true,
      selectedItemId:
        selectedValue === null
          ? null
          : typeof selectedValue === 'object' && 'valueFrom' in selectedValue && selectedValue.valueFrom
            ? selectedValue.valueFrom
            : typeof selectedValue === 'object' && 'value' in selectedValue && selectedValue.value
              ? ''
              : null,
      callback: (id: string, name: string, object: any) => {
        if (id === '') {
          actionData[fieldId] = {
            value: '',
          };
        } else if (id === null || id === actionData[fieldId]?.valueFrom) {
          actionData[fieldId] = null;
        } else {
          actionData[fieldId] = {
            valueFrom: id,
          };

          if (id.startsWith('conversation.ticketTypeFields[')) {
            const ticketTypeFieldId = extractTicketId(id);

            if (!this.lookups.customFieldId) {
              this.lookups.customFieldId = {};
            }

            this.lookups.customFieldId[ticketTypeFieldId] = name;
          }
        }
      },
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();
        const options = [
          ...STANDARD_RESOLVABLE_ENTITY_FIELDS,
          ...this.inputFields
            .filter((f) => f.type === CUSTOM_FIELD_TYPES.USER_SELECT || f.type === CUSTOM_FIELD_TYPES.USER_GROUP_SELECT)
            .map((f) => ({
              title: f.label,
              id: `customMap['${f.id}']`,
              object: {
                type: f.type === CUSTOM_FIELD_TYPES.USER_SELECT ? ENTITY_TYPES.USER : ENTITY_TYPES.USER_GROUP,
              },
            })),
        ].filter((f) => !entityType || f.object.type === entityType);

        const ticketTypesResponse = (await this.customFieldsService.list({
          limit: null,
          order: ['name'],
          descending: false,
          where: [
            {
              field: 'type',
              operator: 'in',
              value: [
                !entityType || entityType === ENTITY_TYPES.USER ? CUSTOM_FIELD_TYPES.USER_SELECT : undefined,
                !entityType || entityType === ENTITY_TYPES.USER_GROUP
                  ? CUSTOM_FIELD_TYPES.USER_GROUP_SELECT
                  : undefined,
              ].filter(Boolean),
            },
          ],
        })) as ListResponse<CustomField>;

        ticketTypesResponse.data.map((f) => {
          options.push({
            title: f.label,
            id: `conversation.ticketTypeFields['${f.id}']`,
            object: {},
          });
        });

        options.push({
          title: !entityType
            ? 'Specific User or User Group'
            : entityType === ENTITY_TYPES.USER
              ? 'Specific User'
              : 'Specific User Group',
          id: '',
          object: {},
        });

        return options.filter((f) => !cleanQuery || f.title.toLowerCase().includes(cleanQuery.toLowerCase()));
      },
    });
  }

  selectSpecificEntity(
    actionData: any,
    fieldId: string,
    entityType?: typeof ENTITY_TYPES.USER | typeof ENTITY_TYPES.USER_GROUP,
    requireSlackId = false,
  ) {
    this.searchablePopoverService.entities({
      event,
      type: entityType,
      selectedEntityId:
        actionData[fieldId] && typeof actionData[fieldId] === 'object' && 'value' in actionData[fieldId]
          ? actionData[fieldId].value
          : actionData[fieldId],
      requireSlackId,
      showNoneOption: true,
      callback: (id: string, name: string, _object: any) => {
        actionData[fieldId] = {
          value: id,
        };

        if (!this.lookups.assigneeId) {
          this.lookups.assigneeId = {};
        }

        this.lookups.assigneeId[id] = name;
      },
    });
  }

  getResolvableEntityLabel(
    resolvableEntity: any,
    entityType?: typeof ENTITY_TYPES.USER | typeof ENTITY_TYPES.USER_GROUP,
  ) {
    const id =
      typeof resolvableEntity === 'object' && 'valueFrom' in resolvableEntity && resolvableEntity.valueFrom
        ? resolvableEntity.valueFrom
        : '';

    if (id.startsWith('conversation.ticketTypeFields[')) {
      const ticketTypeFieldId = extractTicketId(id);

      return this.lookups.customFieldId?.[ticketTypeFieldId];
    }

    if (id.startsWith('customMap[')) {
      const inputFieldId = extractTicketId(id);

      return this.inputFields.find((f) => f.id === inputFieldId)?.label;
    }

    return (
      STANDARD_RESOLVABLE_ENTITY_FIELDS.find((f) => f.id === id)?.title ??
      (!entityType
        ? 'Specific User or User Group'
        : entityType === ENTITY_TYPES.USER
          ? 'Specific User'
          : 'Specific User Group')
    );
  }

  showSpecificEntitySelection(actionData: any, fieldId: string) {
    return actionData[fieldId] && (typeof actionData[fieldId] !== 'object' || !('valueFrom' in actionData[fieldId]));
  }

  async selectCustomField(data: any, event: any) {
    // Use the service to show a popover listing available fields
    this.searchablePopoverService.customFields({
      event,
      allowedTypes: [
        CUSTOM_FIELD_TYPES.CHECKBOX,
        CUSTOM_FIELD_TYPES.SINGLE_SELECT,
        CUSTOM_FIELD_TYPES.MULTI_SELECT,
        CUSTOM_FIELD_TYPES.DATE,
        CUSTOM_FIELD_TYPES.USER_SELECT,
        CUSTOM_FIELD_TYPES.MULTI_USER_SELECT,
        CUSTOM_FIELD_TYPES.USER_GROUP_SELECT,
        CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT,
      ],
      projectId: this.projectId,
      callback: (id: string, name: string, object: any) => {
        if (object) {
          // Check if the selected field is different from the current one
          if (data.customFieldId !== id) {
            data.customFieldId = id; // Update the ID in the step data
            this.selectedCustomFieldForAction = object; // Update the selected field object
            data.value = null; // Reset the value when the field changes
            this.hydratedCustomFieldValue = null; // Reset hydrated value too
            // Initialize checkbox to false
            if (this.selectedCustomFieldForAction?.type === CUSTOM_FIELD_TYPES.CHECKBOX) {
              data.value = false;
              this.hydratedCustomFieldValue = false;
            }
            this.changeDetectorRef.markForCheck(); // Trigger UI update
          }
        }
      },
    });
  }

  // Handles the output event from the AutomationCustomFieldInputComponent
  onCustomFieldValueChange(newValue: any) {
    if (this.step?.data?.action?.type === AUTOMATION_ACTION_TYPES.SET_CUSTOM_FIELD) {
      // Update the hydrated value for immediate display feedback in the child
      this.hydratedCustomFieldValue = newValue;

      // Extract the ID(s) to save to the actual step data
      switch (this.selectedCustomFieldForAction?.type) {
        case CUSTOM_FIELD_TYPES.MULTI_USER_SELECT:
        case CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT:
          // Ensure newValue is an array and map safely
          this.step.data.action.data.value = Array.isArray(newValue) ? newValue.map((v: any) => v?.id) : [];
          break;
        case CUSTOM_FIELD_TYPES.USER_SELECT:
        case CUSTOM_FIELD_TYPES.USER_GROUP_SELECT:
          this.step.data.action.data.value = newValue?.id;
          break;
        case CUSTOM_FIELD_TYPES.SINGLE_SELECT:
        case CUSTOM_FIELD_TYPES.MULTI_SELECT:
          if (this.selectedCustomFieldForAction?.externalSelect) {
            // External select emits {id, label} or array of them
            if (Array.isArray(newValue)) {
              this.step.data.action.data.value = newValue
                .map((v: any) => v?.id)
                .filter((v) => v !== null && v !== undefined);
            } else {
              // Get the 'value' property from the single object (remains null/undefined if newValue is null)
              this.step.data.action.data.value = newValue?.id;
            }
          } else {
            // Internal select emits the primitive value directly
            this.step.data.action.data.value = newValue;
          }
          break;
        default:
          // Includes text, date, boolean - newValue is the primitive
          this.step.data.action.data.value = newValue;
      }
      this.changeDetectorRef.markForCheck();
    }
  }

  async openEmojiPicker(ev: any) {
    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: async (emoji: any) => {
          popover.dismiss();
          this.step.data.action.data.emoji = emoji.id;
          this.hydratedEmojiValue = await this.emojiService.resolveEmojiToDisplay(emoji.id);
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event: ev,
    });

    await popover.present();
  }

  toggleAdvancedSettings() {
    this.showAdvancedSettings = !this.showAdvancedSettings;
  }

  selectAbortEvent(wait: any) {
    // Events to exclude from the list
    const includedEvents = [
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].CLOSED,
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].PUT_ON_HOLD,
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].REOPENED,
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].MESSAGE_ADDED,
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].INTERNAL_MESSAGE_ADDED,
      AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION].EXTERNAL_MESSAGE_ADDED,
    ] as string[];

    // Create the list of available events, filtering out excluded ones
    const eventOptions = Object.entries(AUTOMATION_TRIGGER_EVENTS[AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION])
      .filter(([key, value]) => includedEvents.includes(value))
      .map(([key, value]) => ({
        id: value,
        title: this.formatEventLabel(value),
        objectType: AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION,
      }));

    this.searchablePopoverService.showPopover({
      event,
      showImages: false,
      showSearch: true,
      selectedItemId: null,
      callback: (eventId: string, _name: string, object: any) => {
        if (!wait.abortOn) {
          wait.abortOn = [];
        }

        // Use the hardcoded objectType since object is null
        const objectType = AUTOMATION_TRIGGER_OBJECT_TYPES.CONVERSATION;

        // Check if this event is already added
        const exists = wait.abortOn.some((item: any) => item.event === eventId && item.objectType === objectType);

        if (!exists) {
          wait.abortOn = [
            ...wait.abortOn,
            {
              event: eventId,
              objectType: objectType,
            },
          ];
        }
      },
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim().toLowerCase();

        return eventOptions.filter((option) => !cleanQuery || option.title.toLowerCase().includes(cleanQuery));
      },
    });
  }

  removeAbortEvent(wait: any, index: number) {
    if (wait.abortOn) {
      wait.abortOn = wait.abortOn.filter((_, i) => i !== index);
    }
  }

  formatAbortEventLabel(abortEvent: { event: string; objectType: string }): string {
    return this.formatEventLabel(abortEvent.event);
  }

  private formatEventLabel(event: string): string {
    // Convert camelCase to readable format
    const formatted = event
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    // Special cases for better readability
    const replacements: Record<string, string> = {
      'Internal Message Added': 'Agent Message Added',
      'External Message Added': 'Customer Message Added',
      'Internal Unresponsive': 'Agent Unresponsive',
      'External Unresponsive': 'Customer Unresponsive',
    };

    return replacements[formatted] || formatted;
  }

  // Jira Task Creation Methods
  selectJiraProject(actionData: any) {
    this.searchablePopoverService.showPopover({
      event,
      showImages: false,
      showSearch: true,
      selectedItemId: actionData.projectKey || null,
      callback: (id: string, _name: string, _object: any) => {
        actionData.projectKey = id || null;
      },
      loadData: async (searchText: string) => {
        const cleanQuery = searchText?.trim();

        // Get available Jira projects from the tenant's Jira integration
        const jiraConfig = this.authService.tenant.integrations?.jira;

        if (!jiraConfig?.enabled) {
          return [
            {
              title: 'Jira integration not configured',
              id: null,
              object: {},
            },
          ];
        }

        // The projectKeys are stored in the params object from the integration registration
        const projectKeys = jiraConfig.userInput?.projectKeys?.split(',').map((key) => key.trim()) || [];

        const options = projectKeys.map((key) => ({
          title: key,
          id: key,
          object: {},
        }));

        return options.filter((f) => !cleanQuery || f.title.toLowerCase().includes(cleanQuery.toLowerCase()));
      },
    });
  }
}
